import { useState } from 'react';

import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  AttachmentType,
  NoteStatus,
} from '~/lib/constants/AddPatientNoteModal.constants';
import {
  getSessionSummaryError,
  validateEditNoteFormFields,
} from '~/components/maud/patients/note/ValidateEditNoteFormFields';
import { getSigningBlockers } from '~/components/maud/patients/note/getSigningBlockers';
import { FieldErrorState } from '~/components/maud/patients/note/EditNoteForm';
import { NoteEvents } from '~/analytics';
import { ChakraGrid, ChakraGridItem, FAIconName } from '@equip.health/ui';
import ConfirmModal from '~/components/common/ConfirmModal';
import Loading from '~/pages/loading';
import { EditPatientNoteForm } from '~/components/maud/patients/note/EditPatientNoteForm';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { useFormikContext } from 'formik';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { EditPatientNoteFooter } from '~/components/maud/patients/note/EditPatientNoteFooter';
import { useLatestNoteTemplateRevisionsWithLegacyHandling } from '~/components/maud/patients/note/useLatestNoteTemplateRevisionsWithLegacyHandling';
import { useFlags } from 'launchdarkly-react-client-sdk';

export const EditNoteDrawerBody = () => {
  const { values: formValues } = useFormikContext<EditNoteFormData>();
  const {
    isLoading: isContextApiLoading,
    note,
    noteTemplateFormModel,
    clearAutosaveDebounce,
    closeNoteDrawerWithoutSaving,
    uploadedFiles,
    updateNote,
    isUpdateNoteLoading,
    resetAutosaveError,
    fieldErrors,
    setFieldErrors,
    noteContentEditorRef,
    sessionSummaryEditorRef,
    sessionSummarySectionRef,
    checkCarePlanReadiness,
    carePlanSectionRef,
    setShowFrequencyErrors,
  } = useEditPatientNoteFormContext();

  const {
    cpFrequencyNoteSigningLogic,
    carePlanM4MedPsychNotes: isCarePlanM4MedPsychNotesEnabled,
  } = useFlags<LDFlags>();
  const { trackEvent } = useAnalytics();

  const [openFinalizeModal, setOpenFinalizeModal] = useState<boolean>(false);

  const [uploadInProgressForSignError, setUploadInProgressForSignError] =
    useState<boolean>(false);

  const { selectedRevision } = useLatestNoteTemplateRevisionsWithLegacyHandling(
    formValues.noteTemplateRevision?.node,
  );

  const selectedNoteTemplate = selectedRevision?.template;

  const isMedPsychTemplate =
    selectedNoteTemplate?.templateCode === 'FUP_MED_TEAM' ||
    selectedNoteTemplate?.templateCode === 'INTAKE_MED_TEAM';

  // Med/psych templates gate on the LD flag alone (DB flags not yet populated
  // for these templates); non-med templates keep the DB-flag rule.
  const shouldValidateCarePlan = isMedPsychTemplate
    ? isCarePlanM4MedPsychNotesEnabled
    : !!selectedNoteTemplate?.showCarePlan;

  const resetAllError = () => {
    resetAutosaveError();
    setUploadInProgressForSignError(false);
    setFieldErrors((prev) => ({ ...prev, carePlanError: null }));
    setShowFrequencyErrors(false);
  };

  const validateCarePlanForSigning = (shouldScroll: boolean) => {
    if (!shouldValidateCarePlan) return true;

    const isCarePlanReady = checkCarePlanReadiness();

    if (!isCarePlanReady) {
      const errorMessage = cpFrequencyNoteSigningLogic
        ? 'Please assess the missing frequencies before signing the note.'
        : 'Please assess all frequencies before signing the note.';

      setFieldErrors((prev) => ({
        ...prev,
        carePlanError: errorMessage,
      }));
      setShowFrequencyErrors(true);

      // If a frequency field is required, scroll to it, else scroll to care plan section as a whole
      requestAnimationFrame(() => {
        if (shouldScroll && carePlanSectionRef.current) {
          const firstEmptyRequiredField =
            carePlanSectionRef.current.querySelector('[data-is-ready="false"]');

          if (firstEmptyRequiredField && cpFrequencyNoteSigningLogic) {
            firstEmptyRequiredField.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });

            const selectInput = firstEmptyRequiredField.querySelector('input');
            if (selectInput) {
              selectInput.focus();
            }
          } else {
            // Fallback: scroll to care plan section
            carePlanSectionRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      });
    }

    return isCarePlanReady;
  };

  const uploadInProgressFiles = (uploadedFiles ?? []).filter(
    (attachment: AttachmentType) => !attachment.attachmentExternalId,
  );

  /** Method get called whenever form get updated
   * Reason for having this method is, Formik onChange method is not get called automatically, when Custom Field is updated.
   * This onFormChange will be called whenever any Custom Field value get updated.
   * */
  const onFormChange = () => {
    const { titleError, editorError, typeError, dateError, summaryError } =
      fieldErrors;
    if (titleError || editorError || typeError || dateError || summaryError) {
      validateEditNoteFormFields({
        formValues,
        noteContentEditorRef,
        sessionSummaryEditorRef,
        updateFieldErrorReducer: setFieldErrors,
        shouldValidateSessionSummary: selectedNoteTemplate?.showSessionSummary,
      });
    }
  };

  // Function called on clicking "Sign & Finalize"
  const onSaveSigned = () => {
    resetAllError();

    const isTemplateValid =
      selectedRevision && noteTemplateFormModel
        ? noteTemplateFormModel.validate(true, true)
        : true;

    // Always runs, even when the template has empty required fields, so a blank
    // session summary is still flagged
    let validatedFieldErrors: FieldErrorState;
    const formIsInvalid = validateEditNoteFormFields({
      formValues,
      noteContentEditorRef,
      sessionSummaryEditorRef,
      // Tapped on the way through: the validator only returns a boolean, and
      // fieldErrors from context is a stale render behind at this point
      updateFieldErrorReducer: (errors) => {
        validatedFieldErrors = errors;
        setFieldErrors(errors);
      },
      shouldValidateSessionSummary: selectedNoteTemplate?.showSessionSummary,
    });

    // Title/type/date live outside the scrollable pane and are always visible,
    // so only a blank session summary needs a scroll. It renders above the care
    // plan, so it wins the scroll when both are invalid.
    const hasSummaryError = !!getSessionSummaryError({
      formValues,
      sessionSummaryEditorRef,
      shouldValidateSessionSummary: selectedNoteTemplate?.showSessionSummary,
    });

    if (hasSummaryError) {
      requestAnimationFrame(() => {
        sessionSummarySectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        // scrollIntoView: false, else the editor re-scrolls to the caret and
        // hides the section heading and toolbar above it
        sessionSummaryEditorRef.current?.commands.focus(null, {
          scrollIntoView: false,
        });
      });
    }

    const isCarePlanValid = validateCarePlanForSigning(!hasSummaryError);

    if (
      formIsInvalid ||
      !isCarePlanValid ||
      !isTemplateValid ||
      uploadInProgressFiles.length
    ) {
      // Some blockers (collapsed care plan rows, off-screen template questions)
      // have no visible error, so spell them all out for support and QA
      console.warn(
        'Note cannot be signed:',
        getSigningBlockers({
          fieldErrors: validatedFieldErrors,
          isCarePlanValid,
          noteTemplateFormModel,
          uploadInProgressFiles,
        }),
      );
    }

    if (formIsInvalid || !isCarePlanValid || !isTemplateValid) {
      return;
    }

    if (uploadInProgressFiles.length) {
      setUploadInProgressForSignError(true);
      return;
    }
    setOpenFinalizeModal(true);
    clearAutosaveDebounce();
  };

  const onSaveSignedConfirm = () => {
    trackEvent(NoteEvents.finalizeNoteModalFinalizeButtonClicked, {
      Source: 'Note Form',
    });
    updateNote(NoteStatus.SIGNED).then(() => {
      closeNoteDrawerWithoutSaving();
    });
  };

  return (
    <>
      <ChakraGrid
        height="100%"
        padding="20px 0px 0px 0px"
        templateRows="1fr auto"
      >
        {!note || isContextApiLoading || !formValues ? (
          <Loading />
        ) : (
          <>
            <ChakraGridItem overflowY="auto">
              <ConfirmModal
                body="Are you sure you want to finalize this note? This action cannot be undone."
                cancelIcon={FAIconName.XmarkRegular}
                cancelLabel="Cancel"
                confirmIcon={FAIconName.checkRegular}
                confirmLabel="Finalize"
                isLoading={isUpdateNoteLoading}
                isOpen={openFinalizeModal}
                onClose={() => {
                  trackEvent(NoteEvents.finalizeNoteModalCancelButtonClicked, {
                    Source: 'Note Form',
                  });
                  setOpenFinalizeModal(false);
                }}
                onConfirm={onSaveSignedConfirm}
                title="Finalize note"
              />
              <EditPatientNoteForm onFormChange={onFormChange} />
            </ChakraGridItem>
            <ChakraGridItem>
              <EditPatientNoteFooter
                onSaveSigned={onSaveSigned}
                uploadInProgressForSignError={uploadInProgressForSignError}
              />
            </ChakraGridItem>
          </>
        )}
      </ChakraGrid>
    </>
  );
};
